import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Route as RouteIcon, ScanLine, CheckCircle2, Clock3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Driver home page — first thing a driver sees after login.
 * Three primary actions: open the route view, open the package scanner,
 * open the timesheet.
 *
 * NOTE: The "Go Online" toggle was removed. A driver is now automatically
 * considered ONLINE while they have an active route (status `in_progress`
 * or `paused`). See `useDriverHeartbeat` mounted globally in `App.js`.
 */
export default function DriverHome() {
  const { user } = useAuth();

  return (
    <div className="px-4 sm:px-6 py-8 max-w-md mx-auto" data-testid="driver-home">
      <header className="mb-7">
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Driver</div>
        <h1 className="mt-1 text-3xl font-display font-bold tracking-tight">
          Welcome back, <span className="text-primary">{user?.name?.split(" ")[0] || "Driver"}</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5">
          Pick where you want to start today.
        </p>
      </header>

      {/* 1) Routes */}
      <Link to="/driver/routes" data-testid="driver-home-routes-btn">
        <Card className="p-5 rounded-2xl mb-4 border-2 border-[#3a86ff]/30 bg-[#e0f0ff] hover:bg-[#cce4ff] transition-colors active:scale-[.99]">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-[#3a86ff] text-white grid place-items-center shrink-0">
              <RouteIcon className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#1f4ea3]">
                My deliveries
              </div>
              <div className="text-lg font-semibold text-[#1f4ea3]">Routes</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                See your stops, start the route, capture POD.
              </div>
            </div>
            <CheckCircle2 className="h-5 w-5 text-[#1f4ea3]/70 shrink-0" />
          </div>
        </Card>
      </Link>

      {/* 2) Scan */}
      <Link to="/scan" data-testid="driver-home-scan-btn">
        <Card className="p-5 rounded-2xl border-2 border-[#7C3AED]/30 bg-[#ede4ff] hover:bg-[#ddc9ff] transition-colors active:scale-[.99]">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-[#7C3AED] text-white grid place-items-center shrink-0">
              <ScanLine className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5B21B6]">
                Pickup intake
              </div>
              <div className="text-lg font-semibold text-[#5B21B6]">Packages Scan</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                Scan a QR / barcode to mark a package received.
              </div>
            </div>
            <CheckCircle2 className="h-5 w-5 text-[#5B21B6]/70 shrink-0" />
          </div>
        </Card>
      </Link>

      {/* 3) Timesheet */}
      <Link to="/driver/timesheet" data-testid="driver-home-timesheet-btn">
        <Card className="p-5 rounded-2xl mt-4 border-2 border-[#0f766e]/30 bg-[#d4f2ee] hover:bg-[#b8e6df] transition-colors active:scale-[.99]">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-[#0f766e] text-white grid place-items-center shrink-0">
              <Clock3 className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#0f4741]">
                My hours
              </div>
              <div className="text-lg font-semibold text-[#0f4741]">Timesheet</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                See your worked days grouped by week.
              </div>
            </div>
            <CheckCircle2 className="h-5 w-5 text-[#0f4741]/70 shrink-0" />
          </div>
        </Card>
      </Link>
    </div>
  );
}
